import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { appendViewQueryToPath } from 'features/Chat/chat-view-query';
import { appendChatUserToChatViewUrl } from 'utils/chat-workflow-url';
import { useChatSidebarLayout } from 'contexts/ChatSidebarLayoutContext';
import { useSelector } from 'store';
import menuItems from 'menu-items';
import NavItem, {
  buildBerryUnifiedNavLinkTo,
  collectViewMenuIds,
  computeIsChatsViewsDirectLink,
  menuUrlMatchesLocation,
} from '../MenuList/NavItem';
import { useGetMenuMaster } from 'api/menu';
import { IconMessageCode } from '@tabler/icons-react';
import { clickPreventDefaultAndStopPropagation } from 'utils/helpers';
import {
  buildBotsNavItem,
  buildUnifiedNavItem,
  folderHasVisibleBuiltinRows,
  isBotsRowVisible,
  isUnifiedRowVisibleInFolder,
  type ChatSidebarScopeTab,
} from 'utils/chat-sidebar-unified-statuses';
import {
  BUILTIN_CUSTOM_VIEWS_FOLDER_ID,
  BUILTIN_UNASSIGNED_FOLDER_ID,
} from 'utils/chat-sidebar-folders-storage';
import { useViewCounts, useCustomViewCounts, useBotsViewCounts } from 'hooks/useViewCounts';
import { useChatFolderUnreadCount, type ChatFolderUnreadTarget } from 'hooks/useChatFolderUnreadCount';
import UnreadIndicator from '../MenuList/NavItem/UnreadIndicator';
import type { CustomView } from 'types/userConfig/userConfig';
import type { NavItemType } from 'types';
import type { AdditionalViewsHandle } from '../MenuList/NavCollapse/AdditionalViews';
import UnifiedChatStatusList from './UnifiedChatStatusList';
import {
  CHAT_SIDEBAR_BOTS_FOLDER_TITLE,
  CHAT_SIDEBAR_CUSTOM_VIEWS_FOLDER_TITLE,
  CHAT_SIDEBAR_UNASSIGNED_FOLDER_TITLE,
  chatSidebarBuiltinFolderTitle,
  type ChatFolderTitle,
} from 'utils/chat-view-context-title';

/**
 * סרגל-צד תצוגות: כל "תיקייה" (לא משויכות / mine / all + תיקיות שהמשתמש יצר) מוצגת כאקורדיון,
 * אחד מעל השני; ניתן לפתוח מספר תיקיות במקביל. בתוך כל תיקייה — "תצוגות".
 *
 * תיקיות והשיוך של תצוגות לתיקיות נשמרים בשרת פר-משתמש (userConfig.chatSidebarLayout);
 * localStorage משמש כ-seed מהיר לעלייה בלבד. ראה ChatSidebarLayoutContext + chat-sidebar-server-sync.
 */

const SCOPE_LABEL_ID: Record<ChatSidebarScopeTab, string> = {
  mine: 'sidebar.nav.chatScopeTabs.mine',
  team: 'sidebar.nav.chatScopeTabs.team',
  all: 'sidebar.nav.chatScopeTabs.all',
};

/** תיקיית «בוטים» המובנית — שורה יחידה; לא ניתן לשייך אליה תצוגות מותאמות */
const BUILTIN_BOTS_FOLDER_ID = 'builtin-bots';

const UI_TRANSITION_MS = 200;

interface ChatViewsScopeAccordionsProps {
  additionalViewsRef: React.RefObject<AdditionalViewsHandle | null>;
  onCreateFolder: () => void;
  onRenameFolder: (folderId: string, currentName: string) => void;
}

/** שורת תצוגה מותאמת בתוך תיקייה — מבוססת NavItem עם הדגשת בחירה + פעולות עריכה/מחיקה */
const FolderCustomViewRow = React.memo(function FolderCustomViewRow({
  view,
  folderTitle,
  additionalViewsRef,
}: {
  view: CustomView;
  folderTitle: ChatFolderTitle;
  additionalViewsRef: React.RefObject<AdditionalViewsHandle | null>;
}) {
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const selectedChatUserId = useSelector((s) => s.chat.selectedChatUser?.UserDisplayPhoneNumber);
  const rowUser = useSelector((s) => s.user);
  const { pathname, search } = useLocation();
  const [searchParams] = useSearchParams();

  // עריכה/מחיקה — ליוצר התצוגה או לאדמין בלבד (תצוגות משותפות מ-CompanyCustomViews)
  const canManageView =
    rowUser?.Role === 'admin' || !view.ownerUdid || view.ownerUdid === rowUser?.Udid;
  const isAdmin = rowUser?.Role === 'admin';

  const viewsPages = menuItems.items.find((i) => i.id === 'views-pages');
  const viewMenuIds = React.useMemo(() => collectViewMenuIds(viewsPages?.children), [viewsPages?.children]);
  const isChatsDirect = React.useMemo(() => computeIsChatsViewsDirectLink(pathname), [pathname]);

  const counts = useCustomViewCounts(view.id);

  const navItem: NavItemType = {
    id: `sidebar-tab-custom-${view.id}`,
    title: view.name,
    type: 'item',
    noTranslation: true,
    url: `/chats/custom/${view.id}/${selectedChatUserId || ''}`,
    breadcrumbs: false,
    icon: IconMessageCode,
    counterTone: 'muted',
    // דאבל-קליק של אדמין על אייקון התצוגה — דיאלוג שיתוף/נראות פר-משתמש
    onViewIconDoubleClick: isAdmin
      ? (e) => {
          e.preventDefault();
          e.stopPropagation();
          additionalViewsRef.current?.openVisibility(view.id.toString());
        }
      : undefined,
    actionHoverOnly: true,
    action: drawerOpen && canManageView ? (
      <Stack direction="row" spacing={0.5} sx={{ ml: 1, mr: -0.5 }}>
        <IconButton
          size="small"
          onClick={(e) => {
            clickPreventDefaultAndStopPropagation(e);
            additionalViewsRef.current?.openEdit(view.id.toString());
          }}
        >
          <EditOutlinedIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={(e) => {
            clickPreventDefaultAndStopPropagation(e);
            additionalViewsRef.current?.deleteView(view.id.toString());
          }}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Stack>
    ) : undefined,
  };

  const isViewPage = viewMenuIds.includes(navItem.id!);
  const berryOptimized = {
    isSelected: menuUrlMatchesLocation(navItem, pathname, search, isChatsDirect, true),
    to: buildBerryUnifiedNavLinkTo(navItem, searchParams, selectedChatUserId, isViewPage, true),
  };

  return (
    <NavItem
      item={navItem}
      level={1}
      derivedUnreadCount={counts.unread}
      derivedTotalCount={counts.total}
      berryChatSidebarItem
      berryOptimized={berryOptimized}
      chatSidebarFolderTitle={folderTitle}
    />
  );
});

/** מונה unread מצטבר — מוצג בכותרת תיקייה מכווצת בלבד */
const FolderCollapsedUnreadChip = React.memo(function FolderCollapsedUnreadChip({
  target,
  customViews,
  isExpanded,
  statusRows,
}: {
  target: ChatFolderUnreadTarget;
  customViews: CustomView[];
  isExpanded: boolean;
  statusRows: Record<string, boolean>;
}) {
  const unread = useChatFolderUnreadCount(target, customViews, statusRows);
  if (isExpanded || unread <= 0) return null;
  return (
    <Box sx={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', mx: 0.5 }}>
      <UnreadIndicator countOverride={unread} counterTone="urgent" visible />
    </Box>
  );
});

/** שורת תצוגת «בוטים» — שיחות שמוחזקות כרגע אצל בוט, עם מונה ייעודי מהשרת */
const BotsViewRow = React.memo(function BotsViewRow({
  item,
  berryOptimized,
}: {
  item: NavItemType;
  berryOptimized: { isSelected: boolean; to: string };
}) {
  const counts = useBotsViewCounts();
  return (
    <NavItem
      item={item}
      level={1}
      derivedUnreadCount={counts.unread}
      derivedTotalCount={counts.total}
      berryChatSidebarItem
      berryOptimized={berryOptimized}
      chatSidebarFolderTitle={CHAT_SIDEBAR_BOTS_FOLDER_TITLE}
    />
  );
});

const UnassignedQueueViewRow = React.memo(function UnassignedQueueViewRow({
  item,
  berryOptimized,
}: {
  item: NavItemType;
  berryOptimized: { isSelected: boolean; to: string };
}) {
  const counts = useViewCounts('all', 'unassigned');
  return (
    <NavItem
      item={item}
      level={1}
      derivedUnreadCount={counts.unread}
      derivedTotalCount={counts.total}
      berryChatSidebarItem
      berryOptimized={berryOptimized}
      chatSidebarFolderTitle={CHAT_SIDEBAR_UNASSIGNED_FOLDER_TITLE}
    />
  );
});

function ChatViewsScopeAccordions({
  additionalViewsRef,
  onCreateFolder,
  onRenameFolder,
}: ChatViewsScopeAccordionsProps) {
  const theme = useTheme();
  const intl = useIntl();
  const navigate = useNavigate();
  const {
    activeTabId,
    scope,
    setActiveTabId,
    customFolders,
    viewFolderMap,
    removeFolder,
    isFolderExpanded,
    setFolderExpanded,
    perScopeStatusVisibility,
  } = useChatSidebarLayout();
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;

  const customViews = useSelector((s) => s.userConfig.customViews);
  const userViewsConfig = useSelector((s) => s.userConfig.userViewsConfig);
  const currentUser = useSelector((s) => s.user);
  const selectedChatPhone = useSelector((s) => s.chat.selectedChatUser?.UserDisplayPhoneNumber);

  const visibleCustomViews = React.useMemo(() => {
    const me = currentUser?.Udid;
    if (!me) return customViews;
    return customViews.filter((v) => !v.hiddenForApplicationUserUdids?.includes(me));
  }, [customViews, currentUser?.Udid]);

  const visibleBuiltinScopes = React.useMemo<ChatSidebarScopeTab[]>(() => ['mine', 'all'], []);

  const showUnassignedFolder = true;

  const { pathname, search } = useLocation();
  const [searchParams] = useSearchParams();
  const viewsPages = menuItems.items.find((i) => i.id === 'views-pages');
  const viewMenuIds = React.useMemo(() => collectViewMenuIds(viewsPages?.children), [viewsPages?.children]);
  const isChatsDirect = React.useMemo(() => computeIsChatsViewsDirectLink(pathname), [pathname]);

  const unassignedViewItem = React.useMemo<NavItemType>(
    () => buildUnifiedNavItem('all', 'unassigned'),
    [],
  );

  const unassignedBerryOptimized = React.useMemo(
    () => ({
      isSelected: menuUrlMatchesLocation(unassignedViewItem, pathname, search, isChatsDirect, true),
      to: buildBerryUnifiedNavLinkTo(
        unassignedViewItem,
        searchParams,
        selectedChatPhone,
        viewMenuIds.includes(unassignedViewItem.id!),
        true,
      ),
    }),
    [unassignedViewItem, pathname, search, searchParams, selectedChatPhone, isChatsDirect, viewMenuIds],
  );

  const botsViewItem = React.useMemo<NavItemType>(() => buildBotsNavItem(), []);
  const botsRowVisible = isBotsRowVisible(userViewsConfig);
  const botsCounts = useBotsViewCounts();

  const botsBerryOptimized = React.useMemo(
    () => ({
      isSelected: menuUrlMatchesLocation(botsViewItem, pathname, search, isChatsDirect, true),
      to: buildBerryUnifiedNavLinkTo(
        botsViewItem,
        searchParams,
        selectedChatPhone,
        viewMenuIds.includes(botsViewItem.id!),
        true,
      ),
    }),
    [botsViewItem, pathname, search, searchParams, selectedChatPhone, isChatsDirect, viewMenuIds],
  );

  /** קבוצת מזהי התיקיות הקיימות (מובנות + מותאמות) — לזיהוי תצוגות "יתומות" */
  const knownFolderIds = React.useMemo(() => {
    const ids = new Set<string>(['mine', 'all', BUILTIN_UNASSIGNED_FOLDER_ID]);
    customFolders.forEach((f) => ids.add(f.id));
    return ids;
  }, [customFolders]);

  const viewsByFolder = React.useMemo(() => {
    const map = new Map<string, CustomView[]>();
    const orphans: CustomView[] = [];
    for (const view of visibleCustomViews) {
      const assigned = viewFolderMap[view.id.toString()];
      if (assigned && knownFolderIds.has(assigned)) {
        const arr = map.get(assigned) ?? [];
        arr.push(view);
        map.set(assigned, arr);
      } else {
        orphans.push(view);
      }
    }
    // תצוגות ללא שיוך (כולל תצוגות חברה שאחרים יצרו — השיוך הוא פר-משתמש) —
    // מקובצות בתיקיית המערכת «מותאמות»; עד 26.10 הן נפלו ל«שלי» ונראו שם כפריט זר.
    if (orphans.length > 0) {
      map.set(BUILTIN_CUSTOM_VIEWS_FOLDER_ID, orphans);
    }
    return map;
  }, [visibleCustomViews, viewFolderMap, knownFolderIds]);

  /**
   * כמה תצוגות משויכות לכל תיקייה כולל כאלה שהוסתרו למשתמש —
   * מבחין בין תיקייה מותאמת ריקה (מוצגת) לתיקייה שכל התצוגות שלה כובו (מוסתרת).
   */
  const assignedViewCountByFolder = React.useMemo(() => {
    const m = new Map<string, number>();
    for (const view of customViews) {
      const assigned = viewFolderMap[view.id.toString()];
      if (assigned && knownFolderIds.has(assigned)) m.set(assigned, (m.get(assigned) ?? 0) + 1);
    }
    return m;
  }, [customViews, viewFolderMap, knownFolderIds]);

  /** ריחוף על שורת התיקייה — CSS על AccordionSummary לא אמין; state מציג + ופעולות רק בריחוף */
  const [hoveredFolderKey, setHoveredFolderKey] = React.useState<string | null>(null);
  const [folderMenu, setFolderMenu] = React.useState<{
    folderId: string;
    folderName: string;
    anchorEl: HTMLElement;
  } | null>(null);

  const closeFolderMenu = React.useCallback(() => setFolderMenu(null), []);

  /**
   * תפריט מכווץ (28.10): רשימה שטוחה של *כל* השורות שמותר למשתמש לראות — מכל
   * התיקיות, ברצף אחד, בלי כותרות ובלי קשר לפתיחה/סגירה של תיקיות בתפריט הפתוח
   * (מצב הפתיחה הוא נוחות של המצב הפתוח בלבד). ארבעת מנגנוני הסינון של המצב
   * הפתוח חלים כאן במלואם — נראות פר-תיקייה, ViewsConfig מנוהל-אדמין, תצוגות
   * שהוסתרו למשתמש, ותיקייה שכל שורותיה כבויות; אחרת שורות שהוסתרו בכוונה היו
   * דולפות למכווץ. שורת תור השיוך מוצגת פעם אחת בלבד (ולא שוב בתוך «כל השיחות»).
   * הגלילה מגיעה מה-PerfectScrollbar שעוטף את כל התפריט (Sidebar/index.tsx).
   */
  if (!drawerOpen) {
    const collapsedViewRows = (folderViews: CustomView[], folderTitle: ChatFolderTitle) =>
      folderViews.map((view) => (
        <FolderCustomViewRow
          key={view.id}
          view={view}
          folderTitle={folderTitle}
          additionalViewsRef={additionalViewsRef}
        />
      ));

    const unassignedFolderViews = viewsByFolder.get(BUILTIN_UNASSIGNED_FOLDER_ID) ?? [];
    const unassignedRowVisible =
      showUnassignedFolder &&
      isUnifiedRowVisibleInFolder(
        'all',
        'unassigned',
        perScopeStatusVisibility.all?.statusRows,
        userViewsConfig,
      );

    const collapsedBuiltinFolder = (s: ChatSidebarScopeTab) => {
      const folderViews = viewsByFolder.get(s) ?? [];
      const hasBuiltinRows = folderHasVisibleBuiltinRows(
        s,
        perScopeStatusVisibility[s]?.statusRows,
        userViewsConfig,
        unassignedRowVisible,
      );
      if (!hasBuiltinRows && folderViews.length === 0) return null;
      return (
        <React.Fragment key={s}>
          {hasBuiltinRows && (
            <UnifiedChatStatusList folderScope={s} suppressUnassignedRow={unassignedRowVisible} />
          )}
          {collapsedViewRows(folderViews, chatSidebarBuiltinFolderTitle(s))}
        </React.Fragment>
      );
    };

    return (
      <Box sx={{ width: '100%' }}>
        {unassignedRowVisible && (
          <UnassignedQueueViewRow item={unassignedViewItem} berryOptimized={unassignedBerryOptimized} />
        )}
        {collapsedViewRows(unassignedFolderViews, CHAT_SIDEBAR_UNASSIGNED_FOLDER_TITLE)}
        {visibleBuiltinScopes.includes('mine') && collapsedBuiltinFolder('mine')}
        {visibleBuiltinScopes.includes('all') && collapsedBuiltinFolder('all')}
        {botsRowVisible && <BotsViewRow item={botsViewItem} berryOptimized={botsBerryOptimized} />}
        {customFolders.map((folder) => {
          const folderViews = viewsByFolder.get(folder.id) ?? [];
          if (folderViews.length === 0) return null;
          return (
            <React.Fragment key={folder.id}>
              {collapsedViewRows(folderViews, { type: 'text', text: folder.name })}
            </React.Fragment>
          );
        })}
        {collapsedViewRows(
          viewsByFolder.get(BUILTIN_CUSTOM_VIEWS_FOLDER_ID) ?? [],
          CHAT_SIDEBAR_CUSTOM_VIEWS_FOLDER_TITLE,
        )}
      </Box>
    );
  }

  const isFolderHovered = (folderKey: string) => hoveredFolderKey === folderKey;
  const isFolderActionVisible = (folderKey: string) =>
    isFolderHovered(folderKey) || folderMenu?.folderId === folderKey;
  const folderHoverHandlers = (folderKey: string) => ({
    onMouseEnter: () => setHoveredFolderKey(folderKey),
    onMouseLeave: () => setHoveredFolderKey((prev) => (prev === folderKey ? null : prev)),
  });

  const folderActionSx = (folderKey: string) =>
    ({
      display: 'inline-flex',
      alignItems: 'center',
      width: isFolderActionVisible(folderKey) ? 'auto' : 0,
      minWidth: 0,
      overflow: 'hidden',
      opacity: isFolderActionVisible(folderKey) ? 1 : 0,
      pointerEvents: isFolderActionVisible(folderKey) ? 'auto' : 'none',
      transition: 'opacity 0.12s ease',
    }) as const;

  /** תואם Berry: ListItemButton (py 10px) + שורות תצוגה (mb 0.5, marginInline 8px, borderRadius 8px) */
  const accordionSx = {
    background: 'transparent',
    backgroundImage: 'none',
    boxShadow: 'none',
    '&:before': { display: 'none' },
    mb: 0.5,
    mx: 1,
    borderRadius: '8px',
    overflow: 'hidden',
    '&.Mui-expanded': {
      mb: 0.5,
      mx: 1,
    },
  } as const;

  /** תצוגות בתוך תיקייה — בלי marginInline כפול; טקסט מקבל את מלוא הרוחב הפנוי */
  const folderViewsListSx = {
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    '& .MuiListItemButton-root': {
      marginInline: '0 !important',
      position: 'relative',
      minHeight: 48,
      px: 1.5,
      py: '10px',
      mb: 0.5,
      gap: '8px',
      minWidth: 0,
      boxSizing: 'border-box',
      alignItems: 'center',
    },
    '& .MuiListItemIcon-root': {
      minWidth: '32px !important',
      width: 32,
      flexShrink: 0,
    },
    '& .MuiListItemText-root': {
      flex: '1 1 auto',
      minWidth: 0,
      maxWidth: '100%',
      my: 0,
    },
    '& .MuiTypography-root': {
      maxWidth: '100%',
    },
    '& .nav-item-action': {
      position: 'absolute',
      insetInlineEnd: 2,
      top: '50%',
      transform: 'translateY(-50%)',
      display: 'flex',
      opacity: 0,
      pointerEvents: 'none',
      transition: 'opacity 0.12s ease',
    },
    '& .MuiListItemButton-root:hover .nav-item-action': {
      opacity: 1,
      pointerEvents: 'auto',
    },
  } as const;

  const accordionDetailsSx = {
    p: 0,
    display: 'block',
    '&.MuiAccordionDetails-root': {
      padding: '2px 0 4px',
    },
  } as const;

  const summarySx = {
    minHeight: 48,
    py: '10px',
    px: 1.5,
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: '8px',
    '& .MuiAccordionSummary-content': {
      my: 0,
      minHeight: 'unset',
      alignItems: 'center',
      gap: 0.5,
      flex: '1 1 auto',
      margin: 0,
    },
    //* חץ מצב (26.10): בלי אינדיקציה, תיקייה סגורה נראתה כשורה רגילה ומשתמשים
    //* לא הבינו שיש בה תוכן; MUI מסובב את ה-wrapper אוטומטית כשפתוח.
    '& .MuiAccordionSummary-expandIconWrapper': {
      color: 'text.secondary',
      marginInlineStart: 0.5,
      flexShrink: 0,
      '& svg': { fontSize: 18 },
    },
    transition: theme.transitions.create(['background-color'], {
      duration: UI_TRANSITION_MS,
      easing: theme.transitions.easing.easeOut,
    }),
    '&:hover': {
      bgcolor: alpha(theme.palette.grey[500], theme.palette.mode === 'dark' ? 0.14 : 0.1),
    },
  } as const;

  /** כותרות תיקייה — מעט קטנות ומודגשות משמות התצוגות (body1) בתוך האקורדיון */
  const folderLabelSx = {
    fontSize: '0.8125rem',
    fontWeight: 600,
    lineHeight: 1.45,
    letterSpacing: '0.01em',
    color: 'text.secondary',
    flex: '1 1 auto',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  } as const;

  const addViewButton = (folderId: string) => (
    <Box component="span" sx={folderActionSx(folderId)}>
      <Tooltip title="הוספת תצוגה לתיקייה" placement="top">
        <IconButton
          size="small"
          aria-label="הוספת תצוגה"
          tabIndex={isFolderHovered(folderId) ? 0 : -1}
          onClick={(e) => {
            clickPreventDefaultAndStopPropagation(e);
            additionalViewsRef.current?.openCreate(folderId);
          }}
          sx={{ width: 26, height: 26, flexShrink: 0 }}
        >
          <AddRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );

  const renderBuiltinFolder = (s: ChatSidebarScopeTab) => {
    const isExpanded = isFolderExpanded(s);
    const folderViews = viewsByFolder.get(s) ?? [];
    // כל התצוגות של התיקייה כבויות (מובנות + מותאמות) — התיקייה כולה מוסתרת
    const hasVisibleBuiltinRows = folderHasVisibleBuiltinRows(
      s,
      perScopeStatusVisibility[s]?.statusRows,
      userViewsConfig,
    );
    if (!hasVisibleBuiltinRows && folderViews.length === 0) return null;
    return (
      <Accordion
        key={s}
        disableGutters
        elevation={0}
        square
        expanded={isExpanded}
        {...folderHoverHandlers(s)}
        onChange={(_e, willExpand) => {
          setFolderExpanded(s, willExpand);
          if (willExpand) setActiveTabId(s);
        }}
        TransitionProps={{ timeout: UI_TRANSITION_MS }}
        sx={accordionSx}
      >
        <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={summarySx}>
          <Typography component="span" sx={folderLabelSx}>
            {intl.formatMessage({ id: SCOPE_LABEL_ID[s] })}
          </Typography>
          <FolderCollapsedUnreadChip
            target={{ type: 'builtin', scope: s }}
            customViews={folderViews}
            isExpanded={isExpanded}
            statusRows={perScopeStatusVisibility[s].statusRows}
          />
        </AccordionSummary>
        <AccordionDetails sx={accordionDetailsSx}>
          {isExpanded && (
            <Box sx={folderViewsListSx}>
              <UnifiedChatStatusList folderScope={s} />
              {folderViews.map((view) => (
                <FolderCustomViewRow
                  key={view.id}
                  view={view}
                  folderTitle={chatSidebarBuiltinFolderTitle(s)}
                  additionalViewsRef={additionalViewsRef}
                />
              ))}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderUnassignedFolder = () => {
    const folderViews = viewsByFolder.get(BUILTIN_UNASSIGNED_FOLDER_ID) ?? [];
    // תור השיוך הוא תור גלובלי — נשלט ע"י נראות «לשיוך לנציג» בתיקיית «כל השיחות»
    // (לא לפי הטאב הפעיל, שגרם להסתרה אקראית לפי איפה שהמשתמש עומד)
    const unassignedRowVisible = isUnifiedRowVisibleInFolder(
      'all',
      'unassigned',
      perScopeStatusVisibility.all?.statusRows,
      userViewsConfig,
    );
    if (!showUnassignedFolder || (!unassignedRowVisible && folderViews.length === 0)) return null;
    const isExpanded = isFolderExpanded(BUILTIN_UNASSIGNED_FOLDER_ID);
    const folderLabel = intl.formatMessage({
      id: 'sidebar.nav.views.unassigned',
      defaultMessage: 'לא משויכות',
    });
    return (
      <Accordion
        key={BUILTIN_UNASSIGNED_FOLDER_ID}
        disableGutters
        elevation={0}
        square
        expanded={isExpanded}
        {...folderHoverHandlers(BUILTIN_UNASSIGNED_FOLDER_ID)}
        onChange={(_e, willExpand) => {
          setFolderExpanded(BUILTIN_UNASSIGNED_FOLDER_ID, willExpand);
          if (willExpand) {
            navigate(
              appendViewQueryToPath(
                appendChatUserToChatViewUrl('/chats/unassigned', selectedChatPhone),
                'all',
              ),
            );
          }
        }}
        TransitionProps={{ timeout: UI_TRANSITION_MS }}
        sx={accordionSx}
      >
        <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={summarySx}>
          <Typography component="span" sx={folderLabelSx}>
            {folderLabel}
          </Typography>
          <FolderCollapsedUnreadChip
            target={{ type: 'unassigned' }}
            customViews={folderViews}
            isExpanded={isExpanded}
            statusRows={perScopeStatusVisibility.all.statusRows}
          />
        </AccordionSummary>
        <AccordionDetails sx={accordionDetailsSx}>
          {isExpanded && (
            <Box sx={folderViewsListSx}>
              <UnassignedQueueViewRow item={unassignedViewItem} berryOptimized={unassignedBerryOptimized} />
              {folderViews.map((view) => (
                <FolderCustomViewRow
                  key={view.id}
                  view={view}
                  folderTitle={CHAT_SIDEBAR_UNASSIGNED_FOLDER_TITLE}
                  additionalViewsRef={additionalViewsRef}
                />
              ))}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  /** תיקיית «בוטים» — שורה יחידה: שיחות שמוחזקות כרגע אצל בוט (מוסתרות משאר התצוגות) */
  const renderBotsFolder = () => {
    if (!botsRowVisible) return null;
    const isExpanded = isFolderExpanded(BUILTIN_BOTS_FOLDER_ID);
    const folderLabel = intl.formatMessage({
      id: 'sidebar.nav.views.botsFolder',
      defaultMessage: 'בוטים',
    });
    return (
      <Accordion
        key={BUILTIN_BOTS_FOLDER_ID}
        disableGutters
        elevation={0}
        square
        expanded={isExpanded}
        {...folderHoverHandlers(BUILTIN_BOTS_FOLDER_ID)}
        onChange={(_e, willExpand) => {
          setFolderExpanded(BUILTIN_BOTS_FOLDER_ID, willExpand);
          if (willExpand) {
            navigate(appendChatUserToChatViewUrl('/chats/bots', selectedChatPhone));
          }
        }}
        TransitionProps={{ timeout: UI_TRANSITION_MS }}
        sx={accordionSx}
      >
        <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={summarySx}>
          <Typography component="span" sx={folderLabelSx}>
            {folderLabel}
          </Typography>
          {!isExpanded && botsCounts.unread > 0 && (
            <Box sx={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', mx: 0.5 }}>
              <UnreadIndicator countOverride={botsCounts.unread} counterTone="urgent" visible />
            </Box>
          )}
        </AccordionSummary>
        <AccordionDetails sx={accordionDetailsSx}>
          {isExpanded && (
            <Box sx={folderViewsListSx}>
              <BotsViewRow item={botsViewItem} berryOptimized={botsBerryOptimized} />
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderCustomFolder = (folder: { id: string; name: string }) => {
    const isExpanded = isFolderExpanded(folder.id);
    const folderViews = viewsByFolder.get(folder.id) ?? [];
    // תיקייה ריקה מוצגת (כדי שאפשר יהיה להוסיף אליה); תיקייה שכל התצוגות שלה הוסתרו — לא
    const assignedTotal = assignedViewCountByFolder.get(folder.id) ?? 0;
    if (assignedTotal > 0 && folderViews.length === 0) return null;
    return (
      <Accordion
        key={folder.id}
        disableGutters
        elevation={0}
        square
        expanded={isExpanded}
        {...folderHoverHandlers(folder.id)}
        onChange={(_e, willExpand) => setFolderExpanded(folder.id, willExpand)}
        TransitionProps={{ timeout: UI_TRANSITION_MS }}
        sx={accordionSx}
      >
        <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={summarySx}>
          <Typography component="span" sx={folderLabelSx}>
            {folder.name}
          </Typography>
          {addViewButton(folder.id)}
          <Box component="span" sx={folderActionSx(folder.id)}>
            <Tooltip title="אפשרויות תיקייה" placement="top">
              <IconButton
                size="small"
                aria-label="אפשרויות תיקייה"
                aria-haspopup="menu"
                aria-expanded={folderMenu?.folderId === folder.id ? 'true' : undefined}
                tabIndex={isFolderActionVisible(folder.id) ? 0 : -1}
                onClick={(e) => {
                  clickPreventDefaultAndStopPropagation(e);
                  setFolderMenu({
                    folderId: folder.id,
                    folderName: folder.name,
                    anchorEl: e.currentTarget,
                  });
                }}
                sx={{ width: 26, height: 26, flexShrink: 0 }}
              >
                <MoreVertIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={accordionDetailsSx}>
          {isExpanded && (
            <Box sx={folderViewsListSx}>
              {folderViews.length > 0 ? (
                folderViews.map((view) => (
                  <FolderCustomViewRow
                    key={view.id}
                    view={view}
                    folderTitle={{ type: 'text', text: folder.name }}
                    additionalViewsRef={additionalViewsRef}
                  />
                ))
              ) : (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', px: 1.25, py: 0.75, lineHeight: 1.4 }}
                >
                  אין תצוגות בתיקייה זו. לחצו על + כדי להוסיף תצוגה.
                </Typography>
              )}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  /**
   * תיקיית המערכת «מותאמות» — כל התצוגות המותאמות שלא שויכו לתיקייה אצל המשתמש
   * (כולל תצוגות חברה שאחרים יצרו). מוצגת רק כשיש יתומות; בלי פעולות שינוי-שם/מחיקה.
   */
  const renderCustomViewsFolder = () => {
    const folderViews = viewsByFolder.get(BUILTIN_CUSTOM_VIEWS_FOLDER_ID) ?? [];
    if (folderViews.length === 0) return null;
    const isExpanded = isFolderExpanded(BUILTIN_CUSTOM_VIEWS_FOLDER_ID);
    const folderLabel = intl.formatMessage({
      id: 'sidebar.nav.views.customViewsFolder',
      defaultMessage: 'מותאמות',
    });
    return (
      <Accordion
        key={BUILTIN_CUSTOM_VIEWS_FOLDER_ID}
        disableGutters
        elevation={0}
        square
        expanded={isExpanded}
        {...folderHoverHandlers(BUILTIN_CUSTOM_VIEWS_FOLDER_ID)}
        onChange={(_e, willExpand) => setFolderExpanded(BUILTIN_CUSTOM_VIEWS_FOLDER_ID, willExpand)}
        TransitionProps={{ timeout: UI_TRANSITION_MS }}
        sx={accordionSx}
      >
        <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={summarySx}>
          <Typography component="span" sx={folderLabelSx}>
            {folderLabel}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={accordionDetailsSx}>
          {isExpanded && (
            <Box sx={folderViewsListSx}>
              {folderViews.map((view) => (
                <FolderCustomViewRow
                  key={view.id}
                  view={view}
                  folderTitle={CHAT_SIDEBAR_CUSTOM_VIEWS_FOLDER_TITLE}
                  additionalViewsRef={additionalViewsRef}
                />
              ))}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Box dir="rtl" sx={{ width: '100%', minWidth: 0, boxSizing: 'border-box', pb: 1 }}>
      {renderUnassignedFolder()}
      {visibleBuiltinScopes.includes('mine') && renderBuiltinFolder('mine')}
      {visibleBuiltinScopes.includes('all') && renderBuiltinFolder('all')}
      {renderBotsFolder()}
      {customFolders.map((folder) => renderCustomFolder(folder))}
      {renderCustomViewsFolder()}
      <Menu
        anchorEl={folderMenu?.anchorEl ?? null}
        open={Boolean(folderMenu)}
        onClose={closeFolderMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem
          onClick={() => {
            if (!folderMenu) return;
            const { folderId, folderName } = folderMenu;
            closeFolderMenu();
            onRenameFolder(folderId, folderName);
          }}
        >
          <ListItemIcon>
            <EditOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>שינוי שם</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!folderMenu) return;
            const { folderId } = folderMenu;
            closeFolderMenu();
            removeFolder(folderId);
          }}
        >
          <ListItemIcon>
            <DeleteOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>מחיקת תיקייה</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
}

export default React.memo(ChatViewsScopeAccordions);
